import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  Loader2, Send, Download, ScrollText, Sparkles, Search,
  Bold, Italic, Underline, List, ListOrdered, Heading2, Eye,
  ShieldCheck, RefreshCw, X,
} from "lucide-react";

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return iso || "—"; }
};

/**
 * AdminLetterheadStudio — the "Executive Letterhead Engine" admin surface.
 *
 * Layout: 3-column composer inside a single tab.
 *   1. Template picker (with role-scope pills when applicable)
 *   2. Rich-text composer (contentEditable with bold/italic/list/H2 controls)
 *      + auto-populated recipient context (name, business, vinci_code…)
 *   3. Live PDF preview (iframe re-rendered on demand) + Send CTA + History
 */
export function AdminLetterheadStudio({ api }) {
  const [templates, setTemplates] = useState([]);
  const [tid, setTid] = useState("welcome_kit");
  const [roleScope, setRoleScope] = useState("");
  const [ctx, setCtx] = useState({ name: "", business: "", email: "", vinci_code: "", states: "" });
  const [recipientEmail, setRecipientEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [previewUrl, setPreviewUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const [autoOn, setAutoOn] = useState(false);
  const [history, setHistory] = useState([]);
  const [userSearch, setUserSearch] = useState("");
  const [userResults, setUserResults] = useState([]);
  const editorRef = useRef(null);
  const [dirty, setDirty] = useState(false);

  const current = useMemo(() => templates.find((t) => t.id === tid) || null, [templates, tid]);

  // ---- Load templates + settings + history ----
  const loadTemplates = useCallback(async () => {
    try {
      const [r1, r2, r3] = await Promise.all([
        api.get("/admin/letterhead/templates"),
        api.get("/admin/letterhead/settings"),
        api.get("/admin/letterhead/documents?limit=25"),
      ]);
      setTemplates(r1.data?.templates || []);
      setAutoOn(!!r2.data?.auto_welcome_kit_on_approval);
      setHistory(r3.data?.documents || []);
    } catch { toast.error("Could not load letterhead studio"); }
  }, [api]);
  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  // Seed editor when template flips
  useEffect(() => {
    if (!current) return;
    if (editorRef.current) editorRef.current.innerHTML = current.default_body;
    setSubject(current.subject);
    setRoleScope((current.role_scope || [])[0] || "");
    setDirty(false);
    setPreviewUrl("");
  }, [current]);

  // ---- Recipient search ----
  const searchUsers = useCallback(async (q) => {
    if (!q || q.length < 2) { setUserResults([]); return; }
    try {
      const r = await api.get(`/admin/users?q=${encodeURIComponent(q)}&limit=8`);
      setUserResults(r.data?.users || r.data || []);
    } catch { setUserResults([]); }
  }, [api]);
  useEffect(() => { const t = setTimeout(() => searchUsers(userSearch), 220); return () => clearTimeout(t); }, [userSearch, searchUsers]);

  const pickUser = (u) => {
    const profile = u.profile || {};
    setCtx({
      name: profile.full_name || u.name || u.email?.split("@")[0] || "",
      business: profile.business_name || "",
      email: u.email || "",
      vinci_code: u.vendor_code || u.vinci_code || "",
      states: (profile.authorized_states || []).join(", ") || profile.state || "",
    });
    setRecipientEmail(u.email || "");
    setUserSearch("");
    setUserResults([]);
    setDirty(true);
  };

  // ---- Editor controls ----
  const exec = (cmd, val) => { document.execCommand(cmd, false, val); editorRef.current?.focus(); setDirty(true); };
  const format = (tag) => document.execCommand("formatBlock", false, tag);

  // ---- Preview ----
  const generatePreview = useCallback(async () => {
    if (!current) return;
    setBusy(true);
    try {
      const body_html = editorRef.current?.innerHTML || current.default_body;
      const r = await api.post(
        "/admin/letterhead/render",
        { template_id: tid, role_scope: roleScope || null, body_html, context: ctx },
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(r.data);
      setPreviewUrl(url);
    } catch (e) { toast.error(e?.response?.data?.detail || "Preview failed"); }
    finally { setBusy(false); }
  }, [api, tid, roleScope, ctx, current]);

  // ---- Send ----
  const sendLetter = async () => {
    if (!recipientEmail) { toast.error("Recipient email is required"); return; }
    setSending(true);
    try {
      const body_html = editorRef.current?.innerHTML || current.default_body;
      const r = await api.post("/admin/letterhead/send", {
        template_id: tid, role_scope: roleScope || null, body_html,
        context: ctx, recipient_email: recipientEmail, subject,
      });
      toast.success(`Letterhead delivered to ${recipientEmail} (${r.data.verification_hash})`);
      loadTemplates();
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
    finally { setSending(false); }
  };

  const toggleAuto = async () => {
    try {
      const r = await api.post("/admin/letterhead/settings", { auto_welcome_kit_on_approval: !autoOn });
      setAutoOn(!!r.data?.auto_welcome_kit_on_approval);
      toast.success(r.data?.auto_welcome_kit_on_approval
        ? "Welcome Kit will auto-fire on every partner approval"
        : "Welcome Kit auto-fire OFF");
    } catch { toast.error("Toggle failed"); }
  };

  const grouped = useMemo(() => {
    const g = {};
    templates.forEach((t) => { (g[t.category] ||= []).push(t); });
    return g;
  }, [templates]);

  return (
    <div className="pt-2" data-testid="letterhead-studio">
      {/* Top strip */}
      <div className="flex flex-wrap items-center justify-between gap-4 pb-6 mb-6 border-b border-white/10">
        <div>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-sm bg-[#0e0e0e] border border-[#B87333]/40 flex items-center justify-center">
              <ScrollText size={18} className="text-[#D8B23E]" />
            </div>
            <div>
              <h2 className="font-serif text-3xl md:text-4xl">Letterhead Studio</h2>
              <p className="font-body text-xs text-[#8A8A82] mt-1">
                Obsidian &amp; gold PDFs — signed, sealed, delivered.
              </p>
            </div>
          </div>
        </div>
        <button
          data-testid="letterhead-auto-toggle"
          onClick={toggleAuto}
          className={`rounded-full px-5 py-2.5 font-grotesk text-[11px] uppercase tracking-[0.24em] border transition-all
            ${autoOn
              ? "bg-[#D8B23E] text-black border-[#D8B23E]"
              : "text-[#C9C9C2] border-white/20 hover:border-[#D8B23E]"}`}
        >
          <Sparkles size={12} className="inline mr-2" />
          Auto Welcome Kit: {autoOn ? "ON" : "OFF"}
        </button>
      </div>

      <div className="grid lg:grid-cols-12 gap-6">
        {/* ---- LEFT · Template + Recipient ---- */}
        <div className="lg:col-span-4 space-y-5">
          <div className="border border-white/10 rounded-sm p-4 bg-black/30">
            <div className="text-[10px] text-[#8A8A82] tracking-[0.22em] uppercase mb-3">Template Library</div>
            <div className="space-y-3">
              {Object.entries(grouped).map(([cat, list]) => (
                <div key={cat}>
                  <div className="text-[10px] text-[#B87333] tracking-[0.22em] uppercase mb-1.5">{cat}</div>
                  <div className="grid gap-1.5">
                    {list.map((t) => (
                      <button
                        key={t.id}
                        data-testid={`letterhead-template-${t.id}`}
                        onClick={() => setTid(t.id)}
                        className={`text-left px-3 py-2 rounded-sm border transition-all
                          ${tid === t.id
                            ? "bg-[#D8B23E]/10 border-[#D8B23E] text-[#F5F5F0]"
                            : "border-white/10 hover:border-[#B87333] text-[#C9C9C2]"}`}
                      >
                        <div className="font-body text-sm">{t.label}</div>
                        {t.deep && <div className="text-[10px] text-[#D8B23E] tracking-[0.2em] uppercase mt-0.5">Multi-page · Deep</div>}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {(current?.role_scope || []).length > 0 && (
            <div className="border border-white/10 rounded-sm p-4">
              <div className="text-[10px] text-[#8A8A82] tracking-[0.22em] uppercase mb-3">Role Scope</div>
              <div className="flex flex-wrap gap-1.5">
                {(current.role_scope || []).map((r) => (
                  <button
                    key={r}
                    data-testid={`letterhead-role-${r}`}
                    onClick={() => { setRoleScope(r); setDirty(true); }}
                    className={`px-3 py-1.5 rounded-full font-grotesk text-[10px] uppercase tracking-[0.22em] border transition-colors
                      ${roleScope === r ? "bg-[#B87333] text-black border-[#B87333]" : "border-white/15 text-[#C9C9C2] hover:border-[#B87333]"}`}
                  >{r}</button>
                ))}
              </div>
            </div>
          )}

          <div className="border border-white/10 rounded-sm p-4">
            <div className="text-[10px] text-[#8A8A82] tracking-[0.22em] uppercase mb-3">Recipient</div>
            <div className="relative">
              <div className="flex items-center gap-2 border border-white/10 rounded-sm px-3">
                <Search size={12} className="text-[#8A8A82]" />
                <input
                  data-testid="letterhead-user-search"
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  placeholder="Search by name, email or business…"
                  className="bg-transparent w-full py-2 outline-none font-body text-sm"
                />
              </div>
              {userResults.length > 0 && (
                <div className="absolute z-10 left-0 right-0 mt-1 bg-[#0e0e0e] border border-white/10 rounded-sm max-h-60 overflow-y-auto">
                  {userResults.slice(0, 8).map((u) => (
                    <button
                      key={u.id || u._id || u.email}
                      onClick={() => pickUser(u)}
                      className="w-full text-left px-3 py-2 hover:bg-white/5 border-b border-white/[0.06]"
                    >
                      <div className="font-body text-sm">{u.email}</div>
                      <div className="font-body text-xs text-[#8A8A82]">{(u.profile?.business_name) || u.name || "—"}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="mt-3 grid gap-2">
              {[
                { k: "name", label: "Name" },
                { k: "business", label: "Business" },
                { k: "email", label: "Email" },
                { k: "vinci_code", label: "Vinci Code" },
                { k: "states", label: "Authorised States" },
              ].map((f) => (
                <label key={f.k} className="block">
                  <div className="text-[9px] text-[#8A8A82] tracking-[0.22em] uppercase mb-1">{f.label}</div>
                  <input
                    data-testid={`letterhead-ctx-${f.k}`}
                    value={ctx[f.k] || ""}
                    onChange={(e) => { setCtx({ ...ctx, [f.k]: e.target.value }); setDirty(true); }}
                    className="w-full bg-transparent border border-white/10 rounded-sm px-3 py-2 font-body text-sm outline-none focus:border-[#B87333]"
                  />
                </label>
              ))}
              <label className="block">
                <div className="text-[9px] text-[#8A8A82] tracking-[0.22em] uppercase mb-1">Recipient Email</div>
                <input
                  data-testid="letterhead-recipient-email"
                  value={recipientEmail}
                  onChange={(e) => setRecipientEmail(e.target.value)}
                  className="w-full bg-transparent border border-white/10 rounded-sm px-3 py-2 font-body text-sm outline-none focus:border-[#D8B23E]"
                />
              </label>
            </div>
          </div>
        </div>

        {/* ---- MIDDLE · Editor ---- */}
        <div className="lg:col-span-4 space-y-3">
          <div className="border border-white/10 rounded-sm">
            <div className="flex items-center justify-between px-3 py-2 border-b border-white/10">
              <div className="text-[10px] text-[#8A8A82] tracking-[0.22em] uppercase">Compose</div>
              <div className="flex gap-1">
                {[
                  { icon: Heading2, cmd: () => format("h3"), tid: "fmt-h3" },
                  { icon: Bold, cmd: () => exec("bold"), tid: "fmt-bold" },
                  { icon: Italic, cmd: () => exec("italic"), tid: "fmt-italic" },
                  { icon: Underline, cmd: () => exec("underline"), tid: "fmt-underline" },
                  { icon: List, cmd: () => exec("insertUnorderedList"), tid: "fmt-ul" },
                  { icon: ListOrdered, cmd: () => exec("insertOrderedList"), tid: "fmt-ol" },
                ].map((b, i) => (
                  <button
                    key={i}
                    data-testid={`letterhead-${b.tid}`}
                    onClick={b.cmd}
                    className="w-7 h-7 rounded-sm border border-white/10 hover:border-[#D8B23E] flex items-center justify-center text-[#C9C9C2] hover:text-[#D8B23E]"
                  >
                    <b.icon size={13} />
                  </button>
                ))}
              </div>
            </div>
            <label className="block px-3 pt-3">
              <div className="text-[9px] text-[#8A8A82] tracking-[0.22em] uppercase mb-1">Subject</div>
              <input
                data-testid="letterhead-subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="w-full bg-transparent border-b border-white/10 py-1 font-body text-sm outline-none focus:border-[#B87333]"
              />
            </label>
            <div
              ref={editorRef}
              data-testid="letterhead-editor"
              contentEditable
              suppressContentEditableWarning
              onInput={() => setDirty(true)}
              className="min-h-[380px] max-h-[420px] overflow-y-auto px-4 py-3 font-body text-sm text-[#F5F5F0] outline-none prose-vinci"
              style={{ lineHeight: 1.7 }}
            />
          </div>
          <div className="text-[10px] text-[#8A8A82] tracking-wide">
            Tokens available: <code className="text-[#D8B23E]">{"{name}"}</code>{" "}
            <code className="text-[#D8B23E]">{"{business}"}</code>{" "}
            <code className="text-[#D8B23E]">{"{vinci_code}"}</code>{" "}
            <code className="text-[#D8B23E]">{"{states}"}</code>{" "}
            <code className="text-[#D8B23E]">{"{email}"}</code>
          </div>
        </div>

        {/* ---- RIGHT · Preview + Send + History ---- */}
        <div className="lg:col-span-4 space-y-3">
          <div className="border border-[#D8B23E]/30 rounded-sm bg-black/30">
            <div className="flex items-center justify-between px-3 py-2 border-b border-white/10">
              <div className="text-[10px] text-[#D8B23E] tracking-[0.22em] uppercase">Live Preview</div>
              <div className="flex gap-2">
                <button
                  data-testid="letterhead-preview"
                  onClick={generatePreview}
                  disabled={busy}
                  className="rounded-full border border-white/15 hover:border-[#B87333] px-3 py-1 font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#C9C9C2] hover:text-[#B87333] inline-flex items-center gap-1.5"
                >
                  {busy ? <Loader2 size={11} className="animate-spin"/> : <Eye size={11}/>} Render
                </button>
                {previewUrl && (
                  <a
                    href={previewUrl}
                    download={`vinci-${tid}.pdf`}
                    className="rounded-full border border-white/15 hover:border-[#D8B23E] px-3 py-1 font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#C9C9C2] hover:text-[#D8B23E] inline-flex items-center gap-1.5"
                  >
                    <Download size={11}/> Save
                  </a>
                )}
              </div>
            </div>
            <div className="aspect-[210/297] bg-[#050505]">
              {previewUrl ? (
                <iframe title="Letterhead Preview" src={previewUrl} className="w-full h-full" />
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center text-[#8A8A82] gap-2">
                  <ScrollText size={40} className="opacity-30"/>
                  <div className="text-xs">Click <b className="text-[#D8B23E]">Render</b> to generate the PDF preview</div>
                </div>
              )}
            </div>
          </div>

          <button
            data-testid="letterhead-send"
            onClick={sendLetter}
            disabled={sending || !recipientEmail}
            className="w-full rounded-sm bg-gradient-to-r from-[#B87333] to-[#D8B23E] text-black px-4 py-3 font-grotesk text-xs uppercase tracking-[0.28em] flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {sending ? <Loader2 size={14} className="animate-spin"/> : <Send size={13}/>}
            {sending ? "Sending…" : "Generate & Send"}
          </button>

          <div className="border border-white/10 rounded-sm">
            <div className="flex items-center justify-between px-3 py-2 border-b border-white/10">
              <div className="text-[10px] text-[#8A8A82] tracking-[0.22em] uppercase">Recent Sends</div>
              <button onClick={loadTemplates} className="text-[#8A8A82] hover:text-[#B87333]"><RefreshCw size={11}/></button>
            </div>
            <div className="max-h-[240px] overflow-y-auto divide-y divide-white/[0.06]">
              {history.length === 0 && <div className="p-4 text-xs text-[#8A8A82]">No letterheads sent yet.</div>}
              {history.map((h) => (
                <div key={h.id} className="p-3 flex items-start justify-between gap-3" data-testid={`letterhead-history-${h.id}`}>
                  <div className="min-w-0">
                    <div className="font-body text-sm truncate">{h.template_label}</div>
                    <div className="font-body text-[10px] text-[#8A8A82] truncate">
                      {h.recipient_email} · {fmtDate(h.created_at)}
                    </div>
                    <div className="font-mono text-[10px] text-[#D8B23E] mt-0.5 flex items-center gap-1">
                      <ShieldCheck size={10}/> {h.verification_hash}
                    </div>
                  </div>
                  <a
                    href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/letterhead/documents/${h.id}/pdf`}
                    target="_blank" rel="noreferrer"
                    className="shrink-0 text-[#B87333] hover:text-[#D8B23E]"
                    data-testid={`letterhead-history-open-${h.id}`}
                  >
                    <Download size={13}/>
                  </a>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
